import React from 'react';
import { ArrowRightIcon, CheckIcon, BookOpenIcon, MicIcon, CalendarIcon, LayersIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function ConflictAndConversations() {
  const {
    t
  } = useLanguage();
  const signs = t('conflictAndConversations.signs') as string[];
  const whatWeDoItems = t('conflictAndConversations.whatWeDoItems') as string[];
  return <PageTransition data-id="element-1330">
      <div className="pt-12 pb-24" data-id="element-1331">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1332">
          <DecorativeShape type="blob" color="bg-teal/10" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1333" />
          <Animate className="max-w-3xl" data-id="element-1334">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1335">
              {t('conflictAndConversations.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1336">
              {t('conflictAndConversations.title')}
              <br data-id="element-1337" />
              <span className="text-teal" data-id="element-1338">
                {t('conflictAndConversations.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1339">
              {t('conflictAndConversations.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1340">
              {t('conflictAndConversations.intro2')}
            </p>
            <Link to="/contact" data-id="element-1341">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1343" />} data-id="element-1342">
                {t('conflictAndConversations.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs */}
        <section className="bg-cream py-20 mb-20" data-id="element-1344">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1345">
            <Animate className="mb-12" data-id="element-1346">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1347">
                {t('conflictAndConversations.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1348">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1349">
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1350">
                    <div className="mt-0.5 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-1351">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1352" />
                    </div>
                    <span className="text-gray-700" data-id="element-1353">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* What we work on */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1354">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1355">
            <Animate data-id="element-1356">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1357">
                {t('conflictAndConversations.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1358">
                {t('conflictAndConversations.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1359">
                {t('conflictAndConversations.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1360">
              <div className="space-y-3" data-id="element-1361">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1362">
                    <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-1363">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1364" />
                    </div>
                    <span className="text-gray-700" data-id="element-1365">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* Ways to work together */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1366">
          <Animate className="mb-12" data-id="element-1367">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-1368">
              {t('conflictAndConversations.waysTitle')}
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-1369">
              {t('conflictAndConversations.waysSubtitle')}
            </p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1370">
            {[{
            icon: <BookOpenIcon size={24} className="text-coral" data-id="element-1371" />,
            typeBg: 'bg-coral/10 text-coral',
            typeKey: 'courseLabel',
            titleKey: 'course1Title',
            descKey: 'course1Desc',
            path: '/services/courses/conflict-coaching'
          }, {
            icon: <LayersIcon size={24} className="text-teal" data-id="element-1372" />,
            typeBg: 'bg-teal/10 text-teal',
            typeKey: 'workshopLabel',
            titleKey: 'workshop1Title',
            descKey: 'workshop1Desc',
            path: '/services/workshops'
          }, {
            icon: <MicIcon size={24} className="text-yellow-600" data-id="element-1373" />,
            typeBg: 'bg-yellow-100 text-yellow-700',
            typeKey: 'talkLabel',
            titleKey: 'talk1Title',
            descKey: 'talk1Desc',
            path: '/services/talks'
          }].map((item, i) => <Animate key={i} delay={i * 0.1} data-id="element-1374">
                <Card hoverEffect className="h-full p-6 flex flex-col" data-id="element-1375">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1376">
                    {item.icon}
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${item.typeBg}`} data-id="element-1377">
                      {t(`conflictAndConversations.${item.typeKey}`)}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg text-charcoal mb-2" data-id="element-1378">
                    {t(`conflictAndConversations.${item.titleKey}`)}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1379">
                    {t(`conflictAndConversations.${item.descKey}`)}
                  </p>
                  <Link to={item.path} data-id="element-1380">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRightIcon size={16} data-id="element-1382" />} data-id="element-1381">
                      {t('conflictAndConversations.learnMore')}
                    </Button>
                  </Link>
                </Card>
              </Animate>)}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1383">
          <Animate data-id="element-1384">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-cyan-50 border-0" data-id="element-1385">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1386">
                <CalendarIcon size={32} className="text-teal" data-id="element-1387" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1388">
                {t('conflictAndConversations.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1389">
                {t('conflictAndConversations.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-1390">
                <Button variant="secondary" rightIcon={<ArrowRightIcon size={18} data-id="element-1392" />} data-id="element-1391">
                  {t('conflictAndConversations.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}